import type { ReactNode } from 'react';
import { useModalSearchStore } from '@/modules/Search/hooks/useModalSearchStore';
import Cookies from 'js-cookie';
import { create } from 'zustand';

import type {
  DependencyType,
  HistoriesQuery,
  Theme,
  User,
  VersionStates,
} from '@/components/common/Providers/GraphqlProvider/generated';

type DependencyUser = {
  id: string;
  name: string;
  createdAt: any;
  updatedAt: any;
  deletedAt?: any | null;
  type: DependencyType;
  status: any;
  __typename?: string;
};

type UserStore = {
  user: User | undefined;
  token: string | undefined;
  dependencyId?: string;
  dependencyUser?: DependencyUser;
  saveDependencyId: (id?: string) => void;
  saveDependencyUser: (dependency?: DependencyUser) => void;
  save: (user: any) => void;
  remove: () => void;
};

export const useUserStore = create<UserStore>((set) => ({
  user: undefined,
  token: undefined,
  // dependencyId: localStorage.getItem('dependencyId') || undefined,
  dependencyId: undefined,
  dependencyUser: undefined,
  saveDependencyId: (id) => {
    if (id) {
      Cookies.set('dependencyId', id);
      // localStorage.setItem('dependencyId', id);
    } else {
      // localStorage.removeItem('dependencyId');
      Cookies.remove('dependencyId');
    }
    set((state) => ({ ...state, dependencyId: id }));
  },
  saveDependencyUser: (dependency) => {
    if (dependency) {
      Cookies.set('dependencyUser', JSON.stringify(dependency));
    } else {
      Cookies.remove('dependencyUser');
    }
    set((state) => ({ ...state, dependencyUser: dependency }));
  },
  save: (user) => set((state) => ({ ...state, user: user.user, token: user.token })),
  remove: () => set(() => ({ user: undefined, token: undefined })),
}));

export default useUserStore;

export const useModalLoadingStore = create<{
  loadingVisible: boolean;
  showLoading: (value: boolean) => void;
}>((set) => ({
  loadingVisible: false,
  showLoading: (value: boolean) => set(() => ({ loadingVisible: value })),
}));

export { useModalSearchStore };

export const modalLoadingGetter = useModalLoadingStore.getState;
export const modalLoadingSetter = useModalLoadingStore.setState;

type ModalType = 'success' | 'error' | 'info';

type ModalInfoType = {
  modalVisible: boolean;
  type: ModalType;
  message: string;
  onConfirm?: () => void | undefined;
  showVisible: (info: {
    modalVisible: boolean;
    type?: ModalType;
    message?: string;
    onConfirm?: () => void | undefined;
    reload?: boolean;
  }) => void;
  reload?: boolean;
};

export const useModalInfoStore = create<ModalInfoType>((set) => ({
  modalVisible: false,
  type: 'info',
  message: '',
  onConfirm: undefined,
  reload: false,
  showVisible: (info) =>
    set((state) => ({
      ...state,
      modalVisible: info.modalVisible,
      type: info.type,
      message: info.message,
      onConfirm: info.onConfirm,
      reload: info.reload,
    })),
}));

export const modalInfoGetter = useModalInfoStore.getState;
export const modalInfoSetter = useModalInfoStore.setState;

type ModalDialogType = {
  modalVisible: boolean;
  type: 'info' | 'error' | 'success' | 'create';
  message: string | ReactNode;
  title: string;
  confirmBtnLabel?: string;
  cancelBtnLabel?: string;
  onConfirm: () => void;
  showCancelButton?: boolean;
  showVisible: (info: {
    modalVisible: boolean;
    type?: 'info' | 'error' | 'success' | 'create';
    message?: string | ReactNode;
    title?: string;
    confirmBtnLabel?: string;
    cancelBtnLabel?: string;
    onConfirm?: () => void;
    showCancelButton?: boolean;
  }) => void;
};

export const useModalDialogStore = create<ModalDialogType>((set) => ({
  modalVisible: false,
  type: 'info',
  message: '',
  title: '',
  confirmBtnLabel: 'Confirmar',
  cancelBtnLabel: 'Cancelar',
  showCancelButton: true,
  onConfirm: () => {},
  showVisible: (info) =>
    set((state) => ({
      ...state,
      modalVisible: info.modalVisible,
      type: info.type,
      message: info.message,
      title: info.title,
      onConfirm: info.onConfirm,
      confirmBtnLabel: info.confirmBtnLabel,
      cancelBtnLabel: info.cancelBtnLabel,
      showCancelButton: info.showCancelButton,
    })),
}));

type EditPublishersModalType = {
  modalVisible: boolean;
  pageDynamicId: string | undefined;
  showModal: (info: {
    modalVisible: boolean;
    pageDynamicId?: string;
    dependencyId?: string;
  }) => void;
  dependencyId: string | undefined;
};

export const useEditPublishersModalStore = create<EditPublishersModalType>((set) => ({
  modalVisible: false,
  pageDynamicId: undefined,
  dependencyId: undefined,
  showModal: (info) =>
    set((state) => ({
      ...state,
      modalVisible: info.modalVisible,
      pageDynamicId: info.pageDynamicId,
      dependencyId: info.dependencyId,
    })),
}));

export type THistoryType = HistoriesQuery['histories'][0];

type HistoryType = {
  history?: THistoryType;
  pageDynamicId?: string;
  setHistory: (info: { history?: HistoriesQuery['histories'][0]; pageDynamicId?: string }) => void;
};

export const useHistoryStore = create<HistoryType>((set) => ({
  history: undefined,
  pageDynamicId: undefined,
  setHistory: (info) =>
    set((state) => ({
      ...state,
      history: info.history,
      pageDynamicId: info.pageDynamicId,
    })),
}));

export const modalDialogGetter = useModalDialogStore.getState;
export const modalDialogSetter = useModalDialogStore.setState;

export type PageVersionType = {
  id: string | undefined;
  description: string | undefined;
  state: VersionStates | undefined;
  theme: Theme | null | undefined;
  consecutive: string | null | undefined;
};

type ActivePageType = PageVersionType & {
  save: (page: PageVersionType) => void;
  remove: () => void;
};

export const useActivePageStore = create<ActivePageType>((set) => ({
  id: undefined,
  description: undefined,
  state: undefined,
  theme: undefined,
  consecutive: undefined,
  save: (page) =>
    set((state) => ({
      ...state,
      id: page.id,
      description: page.description,
      state: page.state,
      theme: page.theme,
      consecutive: page.consecutive,
    })),
  remove: () =>
    set(() => ({
      id: undefined,
      description: undefined,
      state: undefined,
      theme: undefined,
      consecutive: undefined,
    })),
}));

export const useSelectedPageStore = create<ActivePageType>((set) => ({
  id: undefined,
  consecutive: undefined,
  description: undefined,
  state: undefined,
  theme: undefined,
  save: (page) =>
    set((state) => ({
      ...state,
      id: page.id,
      description: page.description,
      state: page.state,
      theme: page.theme,
      consecutive: page.consecutive,
    })),
  remove: () =>
    set(() => ({
      id: undefined,
      description: undefined,
      state: undefined,
      theme: undefined,
      consecutive: undefined,
    })),
}));

type ModalCustomType = {
  modalVisible: boolean;
  content?: ReactNode;
  size: 'md' | 'xs' | 'sm' | 'lg' | 'xl' | 'xxl';
  showVisible: (info: {
    modalVisible: boolean;
    content?: ReactNode;
    size?: 'md' | 'xs' | 'sm' | 'lg' | 'xl' | 'xxl';
  }) => void;
};

export const useModalCustomStore = create<ModalCustomType>((set) => ({
  modalVisible: false,
  content: null,
  size: 'md',
  showVisible: (info) =>
    set((state) => ({
      ...state,
      modalVisible: info.modalVisible,
      content: info.content,
      size: info.size,
    })),
}));

export const useModalDynamicStore = create<ModalCustomType>((set) => ({
  modalVisible: false,
  size: 'md',
  showVisible: (info) =>
    set((state) => ({
      ...state,
      modalVisible: info.modalVisible,
      size: info.size,
    })),
}));

export const useModalDialogCustomStore = create<ModalDialogType>((set) => ({
  modalVisible: false,
  type: 'info',
  message: '',
  title: '',
  confirmBtnLabel: 'Confirmar',
  cancelBtnLabel: 'Cancelar',
  onConfirm: () => {},
  showVisible: (info) =>
    set((state) => ({
      ...state,
      modalVisible: info.modalVisible,
      type: info.type,
      message: info.message,
      title: info.title,
      onConfirm: info.onConfirm,
      confirmBtnLabel: info.confirmBtnLabel,
      cancelBtnLabel: info.cancelBtnLabel,
    })),
}));

export const modalDialogCustomGetter = useModalDialogCustomStore.getState;
export const modalDialogCustomSetter = useModalDialogCustomStore.setState;
